import * as React from "react";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemText from "@mui/material/ListItemText";
import ListItemIcon from "@mui/material/ListItemIcon";
import Typography from "@mui/material/Typography";
import { Link, useNavigate } from "react-router-dom";
import { UserContext } from "../auth/auth-context";
import { Staff } from "../../types";
import { useSelectedBusiness } from "../../redux/store";
import Box from "@mui/material/Box";
import AccountCircle from "@mui/icons-material/AccountCircle";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import SupportAgentRoundedIcon from "@mui/icons-material/SupportAgentRounded";
import AdminPanelSettingsRoundedIcon from "@mui/icons-material/AdminPanelSettingsRounded";
import Groups2RoundedIcon from "@mui/icons-material/Groups2Rounded";
import NotificationBadge from "../reminder/notificationBadge";
import { setSelectedBusiness } from "../../redux/actions/businessActions";
import { useDispatch } from "react-redux";


import { APP_IDS, getRuntimeAppProfile } from '../../../apps/registry';

const menuItemSx = {
  minHeight: 60,
  px: 1.25,
  py: 1,
  borderRadius: 3,
  alignItems: "center",
  gap: 1,
  "&:hover": {
    backgroundColor: "rgba(219,234,254,0.55)",
  },
};

const menuIconSx = {
  minWidth: 0,
  width: 34,
  height: 34,
  borderRadius: 2.2,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#15314c",
  backgroundColor: "rgba(219,234,254,0.75)",
  flexShrink: 0,
};

export default function AccountMenu(props: any) {
  const showNotification = props?.showNotification !== false;
  const userContext = React.useContext(UserContext);
  const dispatch = useDispatch();
  const providerUser = userContext.providerUser;
  const selectedBusiness = useSelectedBusiness();
  const navigate = useNavigate();

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);



  React.useEffect(() => {
    if (providerUser?.defaultBusinessId && !selectedBusiness) {
      dispatch(setSelectedBusiness(providerUser.defaultBusinessId));
    }
  }, [providerUser?.defaultBusinessId, selectedBusiness, dispatch]);

  const accountMenuOpen = Boolean(anchorEl);

  const handleAccountMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleAccountMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    userContext.logout();
    navigate("/");
    window.location.reload();
  };

  const currentStaffImage = selectedBusiness?.businessStaffs?.find(
    (staff: Staff) => Number(staff.id) === providerUser?.staffId,
  )?.image;

  const email = providerUser?.email;
  const canOpenAgentWorkspace =
    providerUser?.isAgent === true ||
    providerUser?.isDefaultConcierge === true ||
    providerUser?.isPlatformAdmin === true;
  const canOpenBusinessAdmin = providerUser?.isPlatformAdmin === true;

  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      {showNotification && providerUser?.staffId && (
        <NotificationBadge currentStaffImage={currentStaffImage} email={email} />
      )}

      {/* Profile image triggers the account menu */}
      <Box
        onClick={handleAccountMenuClick}
        sx={{
          cursor: "pointer",
          ml: showNotification ? 2 : 0,
          width: 38,
          height: 38,
          borderRadius: "999px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          border: accountMenuOpen
            ? "1px solid rgba(21,49,76,0.22)"
            : "1px solid rgba(255,255,255,0.16)",
          backgroundColor: accountMenuOpen
            ? "rgba(255,255,255,0.96)"
            : "rgba(255,255,255,0.10)",
          boxShadow: accountMenuOpen
            ? "0 14px 28px rgba(15,31,46,0.10)"
            : "0 8px 20px rgba(6,16,28,0.12)",
          transition: "all 180ms ease",
          "&:hover": {
            backgroundColor: accountMenuOpen ? "#ffffff" : "rgba(255,255,255,0.16)",
            transform: "translateY(-1px)",
          },
        }}
      >
        {currentStaffImage ? (
          <img
            src={currentStaffImage.url}
            alt="Profile"
            style={{
              width: 30,
              height: 30,
              borderRadius: "50%",
              objectFit: "cover",
            }}
          />
        ) : (
          <AccountCircle
            sx={{
              fontSize: 28,
              color: accountMenuOpen ? "#15314c" : "#f5f8fc",
            }}
          />
        )}
      </Box>

      {/* Account Menu */}
      <Menu
        id="account-menu"
        anchorEl={anchorEl}
        open={accountMenuOpen}
        onClose={handleAccountMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        sx={{
          "& .MuiPaper-root": {
            display: "flex",
            flexDirection: "column",
            minWidth: 220,
            maxWidth: 260,
            maxHeight: 320,
            overflowY: "auto",
            borderRadius: "18px",
            border: "1px solid rgba(21,49,76,0.10)",
            background:
              "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(246,249,255,0.98) 100%)",
            boxShadow: "0 24px 50px rgba(15,31,46,0.14)",
            mt: 1.25,
            p: 1,
          },
        }}
      >
        <Box sx={{ px: 1.25, pt: 0.75, pb: 0.9 }}>
          <Typography sx={{ fontSize: "0.78rem", fontWeight: 800, color: "#15314c", letterSpacing: "0.04em" }}>
            ACCOUNT
          </Typography>
          <Typography sx={{ mt: 0.35, fontSize: "0.88rem", color: "#607388" }}>
            Manage your profile and schedule
          </Typography>
        </Box>
        <MenuItem
          onClick={handleAccountMenuClose}
          component={Link}
          to="/profile"
          sx={menuItemSx}
        >
          <ListItemIcon sx={menuIconSx}>
            <PersonOutlineRoundedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Profile"
            secondary="View and update your account"
            primaryTypographyProps={{ fontSize: "0.92rem", fontWeight: 700, color: "#15314c" }}
            secondaryTypographyProps={{ fontSize: "0.77rem", color: "#607388" }}
          />
        </MenuItem>

        {canOpenAgentWorkspace && (
          <MenuItem
            onClick={handleAccountMenuClose}
            component={Link}
            to="/care/agent"
            sx={menuItemSx}
          >
            <ListItemIcon sx={menuIconSx}>
              <SupportAgentRoundedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary="Agent Workspace"
              secondary="Open workspace"
              primaryTypographyProps={{ fontSize: "0.92rem", fontWeight: 700, color: "#15314c" }}
              secondaryTypographyProps={{ fontSize: "0.77rem", color: "#607388" }}
            />
          </MenuItem>
        )}
        {canOpenAgentWorkspace && (
          <MenuItem
            onClick={handleAccountMenuClose}
            component={Link}
            to="/referrer/portal"
            sx={menuItemSx}
          >
            <ListItemIcon sx={menuIconSx}>
              <Groups2RoundedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary="Referrer Portal"
              secondary="Open referrer workspace"
              primaryTypographyProps={{ fontSize: "0.92rem", fontWeight: 700, color: "#15314c" }}
              secondaryTypographyProps={{ fontSize: "0.77rem", color: "#607388" }}
            />
          </MenuItem>
        )}
        {canOpenBusinessAdmin && (
          <MenuItem
            onClick={handleAccountMenuClose}
            component={Link}
            to="/care/admin"
            sx={menuItemSx}
          >
            <ListItemIcon sx={menuIconSx}>
              <AdminPanelSettingsRoundedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary="Business Admin"
              secondary="Manage businesses"
              primaryTypographyProps={{ fontSize: "0.92rem", fontWeight: 700, color: "#15314c" }}
              secondaryTypographyProps={{ fontSize: "0.77rem", color: "#607388" }}
            />
          </MenuItem>
        )}

        <Box sx={{ px: 1.25, py: 0.6 }}>
          <Box sx={{ height: 1, backgroundColor: "rgba(21,49,76,0.08)" }} />
        </Box>
        <MenuItem
          onClick={handleLogout}
          sx={menuItemSx}
        >
          <ListItemIcon
            sx={{
              ...menuIconSx,
              color: "#b42318",
              backgroundColor: "rgba(254,226,226,0.8)",
            }}
          >
            <LogoutRoundedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Logout"
            secondary="Sign out of your account"
            primaryTypographyProps={{ fontSize: "0.92rem", fontWeight: 700, color: "#8a1c1c" }}
            secondaryTypographyProps={{ fontSize: "0.77rem", color: "#b05b5b" }}
          />
        </MenuItem>
      </Menu>

    </Box>
  );
}
